import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import {catchError, retryWhen, shareReplay, tap, take, scan} from 'rxjs/operators';

import { LpAssetListItemReqRes } from 'src/app/lp/assets/LpAssetListItemReqRes.model';
import { HttpService } from 'src/app/services/http.service';
import { InvestmentOpportunityReqRes } from 'src/app/lp/investment-opportunity/models/investmentOpportunityReqRes.model';
import { GpDetailsReqRes } from 'src/app/lp/models/GpDetailsReqRes.model';
import { LoggerService } from 'src/app/shared/errors/logger.service';
import { LpAssetReqRes } from 'src/app/lp/assets/LpAssetReqRes.model';
import HttpStatusCodesEnum from '../../shared/enums/HttpStatusCodesEnum';
import { InvestingEntityReqRes } from '../../dashboard/models/InvestingEntityReqRes.model';

@Injectable()
export class LpAssetDataService {
  public readonly LP_ASSET_BASE_ENDPOINT = 'lp/assets';
  public readonly LP_OPPORTUNITY_BASE_ENDPOINT = 'lp/opportunity';

  constructor(private http: HttpService, private loggerService: LoggerService) { }

  getLpAssetsList(): Observable<LpAssetListItemReqRes[]> {
    return this.http.getTyped<LpAssetListItemReqRes[]>(this.LP_ASSET_BASE_ENDPOINT).pipe(
      shareReplay(),
      retryWhen(error =>
        error.pipe(
          tap(err => {
            if (err.status !== HttpStatusCodesEnum.REQUEST_TIMEOUT) {
              throw error;
            }
          }),
          // retry at most 3 times
          scan((errorCount, err) => {
            if (errorCount > 3) {
              throw error;
            }
            return errorCount + 1;
          }, 1)
        )
      ),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  getById(assetId: number) {
    const url = `${this.LP_ASSET_BASE_ENDPOINT}/${assetId}`;
    return this.http.getTyped<LpAssetReqRes>(url).pipe(
      shareReplay(),
      tap(asset => {
        if (!asset || !asset.gpDetails) {
          this.loggerService.error('LP Asset with missing gpDetails was received. ', [
            'assetId:' + assetId,
            'response', asset
          ]);
        }
      })
    );
  }

  getInvestmentOpportunity(opportunityCode: string) {
    const url = `${this.LP_OPPORTUNITY_BASE_ENDPOINT}/${opportunityCode}`;
    return this.http.getTyped<InvestmentOpportunityReqRes>(url).pipe(
      shareReplay()
    );
  }

  getInvestingEntityDetailsByOpportunityCode(opportunityCode: string): Observable<InvestingEntityReqRes> {
    const url = `${this.LP_OPPORTUNITY_BASE_ENDPOINT}/investorDetails/${opportunityCode}`;
    return this.http.getTyped<InvestingEntityReqRes>(url).pipe(
      shareReplay(1),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  saveInvestmentOpportunity(opportunityCode: string, model: InvestmentOpportunityReqRes) {
    const url = `${this.LP_OPPORTUNITY_BASE_ENDPOINT}/investment/${opportunityCode}`;
    return this.http.putTyped<boolean>(url, model).pipe(
      shareReplay()
    );
  }

  getAssetAgentDetails(assetId: number) {
    const url = `${this.LP_ASSET_BASE_ENDPOINT}/${assetId}/agent-details/`;
    return this.http.getTyped<GpDetailsReqRes>(url).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  getPendingAssets(): Observable<LpAssetListItemReqRes[]> {
    return this.http.getTyped<LpAssetListItemReqRes[]>(`${this.LP_ASSET_BASE_ENDPOINT}/pending`).pipe(
      shareReplay(1),
      retryWhen(error =>
        error.pipe(
          tap(err => {
            if (err.status !== HttpStatusCodesEnum.REQUEST_TIMEOUT) {
              throw error;
            }
          }),
          // retry at most 3 times
          scan((errorCount, err) => {
            if (errorCount > 3) {
              throw error;
            }
            return errorCount + 1;
          }, 1)
        )
      ),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  getAssetsOpportunities(): Observable<LpAssetListItemReqRes[]> {
    return this.http.getTyped<LpAssetListItemReqRes[]>(`${this.LP_ASSET_BASE_ENDPOINT}/opportunities`).pipe(
      shareReplay(1),
      retryWhen(error =>
        error.pipe(
          tap(err => {
            if (err.status !== HttpStatusCodesEnum.REQUEST_TIMEOUT) {
              throw error;
            }
          }),
          // retry at most 3 times
          scan((errorCount, err) => {
            if (errorCount > 3) {
              throw error;
            }
            return errorCount + 1;
          }, 1)
        )
      ),
      catchError(err => {
        return throwError(err);
      })
    );
  }
}
